import {
  type ChangeEvent,
  type ChangeEventHandler,
  type CSSProperties,
  type FocusEventHandler,
  type HTMLAttributes,
  type KeyboardEventHandler,
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react';
import { clsx } from 'clsx';
import hljs from 'highlight.js/lib/core';
import Editor from 'react-simple-code-editor';
import { VerticalStack } from '../../alignment/vertical-stack';
import { ErrorMessage } from '../error-message/error-message';
import { InputHint } from '../input-hint/input-hint';
import { Label } from '../label/label';
import { useFocusWithin } from '../shared/use-focus-within';
import inputStyles from '../input.module.scss';
import styles from './code-editor-input.module.scss';

export type CodeEditorInputSupportedLanguages = 'json' | 'yaml' | 'xml';

export function getDefaultCodeEditorLanguageValue(language: CodeEditorInputSupportedLanguages) {
  switch (language) {
    case 'json':
      return '{}';
    default:
      return '';
  }
}

export type CodeEditorInputProps = HTMLAttributes<HTMLDivElement> & {
  // Props for the component
  error?: string;
  hideLabel?: boolean;
  hint?: ReactNode;
  ignoreTabKey?: boolean;
  insertSpaces?: boolean;
  label: string;
  language: CodeEditorInputSupportedLanguages;
  style?: CSSProperties;
  tabSize?: number;
  touched?: boolean;
  value: string;

  // Props for the textarea
  textareaId?: string;
  textareaClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  form?: string;
  maxLength?: number;
  minLength?: number;
  name?: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  onClick?: MouseEventHandler<HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onKeyUp?: KeyboardEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;

  // Props for the highlighted code's pre element
  preClassName?: string;
};

export function CodeEditorInput({
  className,
  error,
  hideLabel,
  hint,
  id: providedId,
  label,
  language,
  name,
  onChange,
  placeholder,
  required,
  textareaClassName,
  touched,
  value,
  ...rest
}: CodeEditorInputProps) {
  const [loaded, setLoaded] = useState(Boolean(hljs.getLanguage(language)));
  const [hasFocus, handleFocusWithin, handleBlurWithin] = useFocusWithin();
  const generatedId = useId();
  const id = providedId || generatedId;
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const showError = Boolean(touched && error && value);
  const showValue = Boolean(!hideLabel && (value || placeholder || hasFocus));

  useEffect(() => {
    (async function loadInLanguage(l: CodeEditorInputSupportedLanguages) {
      try {
        if (!hljs.getLanguage(l)) {
          switch (l) {
            case 'json': {
              const json = (await import('highlight.js/lib/languages/json')).default;
              hljs.registerLanguage('json', json);
              setLoaded(true);
              break;
            }
            case 'yaml': {
              const yaml = (await import('highlight.js/lib/languages/yaml')).default;
              hljs.registerLanguage('yaml', yaml);
              setLoaded(true);
              break;
            }
            case 'xml': {
              const xml = (await import('highlight.js/lib/languages/xml')).default;
              hljs.registerLanguage('xml', xml);
              setLoaded(true);
              break;
            }
            default:
              break;
          }
        }
      } catch {}
    })(language);
  }, [language]);

  const handleChange = useCallback(
    (val: string) => {
      try {
        onChange?.({ target: { name, value: val } } as ChangeEvent<HTMLTextAreaElement>);
      } catch {}
    },
    [name, onChange],
  );
  const handleHighlight = useCallback(
    (code: string) => {
      try {
        if (loaded) {
          return hljs.highlight(code, { language }).value;
        }

        return code;
      } catch {
        return code;
      }
    },
    [language, loaded],
  );

  return (
    <VerticalStack className={clsx(inputStyles.input, styles.codeEditorInput)} onBlurCapture={handleBlurWithin} onFocusCapture={handleFocusWithin}>
      <Label
        className={styles.label}
        hasError={showError}
        htmlFor={id}
        isFloating={showValue}
        isHidden={hideLabel}
        label={label}
        required={required}
      />

      <Editor
        {...rest}
        aria-describedby={hint ? hintId : undefined}
        aria-errormessage={showError ? errorId : undefined}
        aria-invalid={Boolean(error)}
        aria-required={required}
        className={clsx(styles.container, className)}
        highlight={handleHighlight}
        id={id}
        onValueChange={handleChange}
        placeholder={placeholder}
        preClassName={clsx(styles.pre, showValue && styles.hasValue, rest.preClassName)}
        required={required}
        textareaClassName={clsx(styles.field, showValue && styles.hasValue, textareaClassName)}
        value={value}
      />

      {hint ? <InputHint id={hintId}>{hint}</InputHint> : null}
      {showError ? <ErrorMessage id={errorId}>{error}</ErrorMessage> : null}
    </VerticalStack>
  );
}
